import { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { IChart } from "../../../../../@types/chart.type";
import iconDelete from "./../../../../../assets/_TrashIcon_gray.svg";
import { getCostOfIdleness } from "../../../../../services/api/widgets/widgetsService";
import { IApiResponse } from "../../../../../@types/api-response.type";
import { storageUserGet } from "../../../../../storage/storageUser";
import { ICostOfIdleness } from "../../../../../@types/widgets.type";
import { IconCostIdleness } from "../../../../../icons/IconCostIdleness";
import styles from "./styles.module.scss";

interface CostOfIdlenessProps {
  chart: IChart;
  ismodeEdit: boolean;
  deleteChart: (id: string | undefined, widgetId: string) => void;
  rowHeightGrid: number;
}

export function CostOfIdleness({
  chart,
  deleteChart,
  ismodeEdit,
  rowHeightGrid,
}: CostOfIdlenessProps) {
  const [data, setData] = useState<ICostOfIdleness[]>([]);
  const [loading, setLoading] = useState(true);

  const user = storageUserGet();

  function formatCurrency(value: number, locale = "pt-BR", currency = "BRL") {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(value);
  }

  useEffect(() => {
    async function fetchCostOfIdleness() {
      try {
        setLoading(true);
        const response = (await getCostOfIdleness(
          user.email,
          user?.resourceId as string
        )) as IApiResponse<ICostOfIdleness[]>;

        if (response.statusCode === 200) {
          setData(response.result);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    fetchCostOfIdleness();
  }, [user.email, user?.resourceId]);

  const costs = data.map((value) => value.cost);

  const getMonthAbbreviation = (monthNumber: number) => {
    return new Intl.DateTimeFormat("pt-BR", { month: "short" })
      .format(new Date(2022, monthNumber - 1))
      .replace(".", "")
      .toUpperCase();
  };

  const mapedMonthAndYear = useMemo(() => {
    return data?.map(({ month, year }) => {
      return `${getMonthAbbreviation(month)}/${year}`;
    });
  }, [data]);

  const series = [
    {
      data: costs,
    },
  ];

  const options = {
    chart: {
      type: "bar",
      width: "100%",
      height: { rowHeightGrid },
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        borderRadiusApplication: "end",
      },
    },
    colors: ["#C62828"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: mapedMonthAndYear,
      labels: {
        formatter: (value: string) => {
          return value.length > 10 ? value.slice(0, 10) + "..." : value;
        },
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${formatCurrency(val)}`,
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      custom: function ({
        dataPointIndex,
      }: {
        dataPointIndex: number;
      }) {
        const value = data[dataPointIndex];

        return `
          <div class='labelCustom'>
             <span><b>${getMonthAbbreviation(value.month)}/${
          value.year
        }</b></span>
            <span>Custo: ${formatCurrency(value.cost)}</span>
            <span>Horas ociosas: ${value.idleHours}h</span>
          </div>
        `;
      },
    },
  };

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <h3>
          <IconCostIdleness size={26} />
          Custo Por Ociosidade
        </h3>
      </div>
      <div style={{ width: "100%", height: "100%" }}>
        {loading ? (
          <p style={{ textAlign: "center", color: "#212121" }}>Carregando...</p>
        ) : (
          <>
            {data.length ? (
              <ReactApexChart
                options={options}
                series={series}
                type="bar"
                width="100%"
                height={rowHeightGrid - 46}
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#616161",
                }}
              >
                <p style={{ marginTop: "1rem" }}>Nenhum dado encontrado.</p>
              </div>
            )}
          </>
        )}
      </div>
      {ismodeEdit && (
        <button onClick={() => deleteChart(chart.id, chart.widgetId)}>
          <img src={iconDelete} alt="" />
        </button>
      )}
    </div>
  );
}
